/**
 * WCC360 v2 — reading the canonical (sealed) report.
 *
 * The client-side result is a PREVIEW. The moment the Experience Engine has
 * sealed a `learner_reports` payload for this run, the canonical payload
 * wins and the preview is retired. This module is the only place that
 * decides "is the canonical payload here yet, and what does it say".
 *
 * It performs no scoring of its own — it reads rows and projects fields.
 */
import { supabase } from "@/integrations/supabase/client";

/** Minimal projection of the sealed student payload that the result page needs. */
export interface CanonicalResult {
  generatedAt: string;
  reportHash: string | null;
  /** Top recommendations, best first. Empty for bands that skip recommendations. */
  careers: Array<{
    careerId: string;
    careerName: string;
    cluster: string;
    score: number | null;
    tier: string | null;
    explanation: string | null;
  }>;
  archetypePrimary: string | null;
  archetypeSecondary: string | null;
  payload: Record<string, unknown>;
}

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}
function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}
function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * Fetch the sealed student report for this learner, but only if it was
 * generated at or after `sinceIso` — an older report belongs to a previous
 * run and must never be presented as the result of this one.
 */
export async function fetchCanonicalResult(
  sinceIso: string | null,
): Promise<CanonicalResult | null> {
  const { data: auth } = await supabase.auth.getUser();
  const learner_id = auth?.user?.id;
  if (!learner_id) return null;

  let q = supabase
    .from("learner_reports")
    .select("payload, report_hash, generated_at")
    .eq("learner_id", learner_id)
    .eq("audience", "student")
    .order("generated_at", { ascending: false })
    .limit(1);
  if (sinceIso) q = q.gte("generated_at", sinceIso);

  const { data, error } = await q.maybeSingle();
  if (error || !data?.payload) return null;

  const payload = asRecord(data.payload);
  const recs = Array.isArray(payload.recommendations) ? payload.recommendations : [];
  const careers = recs.map((raw) => {
    const r = asRecord(raw);
    return {
      careerId: str(r.career_id) ?? "",
      careerName: str(r.career_name) ?? "",
      cluster: str(r.career_cluster) ?? "",
      score: num(r.recommendation_score) ?? num(r.fit_score),
      tier: str(r.tier),
      explanation: str(r.explanation),
    };
  }).filter((c) => c.careerId !== "");

  // The sealed payload carries the archetype under one of two shapes
  // depending on the audience adapter version. Read both, invent neither.
  const identity = asRecord(payload.identity);
  const archetype = asRecord(payload.archetype);
  const archetypePrimary =
    str(archetype.primary) ?? str(identity.archetype_primary) ?? str(payload.archetype_primary);
  const archetypeSecondary =
    str(archetype.secondary) ?? str(identity.archetype_secondary) ?? str(payload.archetype_secondary);

  return {
    generatedAt: data.generated_at as string,
    reportHash: str(data.report_hash),
    careers,
    archetypePrimary,
    archetypeSecondary,
    payload,
  };
}
